import { useAuth0 } from '@auth0/auth0-react';
import { Banner, Button, focusRing } from '@ths/ui';
import arrayMutators from 'final-form-arrays';
import { HotelForm as Form } from 'hotel/HotelForm';
import { useLocation, useNavigate } from 'hotel/navigation';
import { times } from 'lodash';
import { useCallback, useRef, useMemo, useState, useEffect } from 'react';
import { Field } from 'react-final-form';

import Card from 'components/Card';
import DuplicateBookingConfirmationModal from 'components/DuplicateBookingConfirmationModal';
import { ClientTeamMemberSelectOption } from 'components/Inputs/FormInputs/ClientTeamMemberSelect';
import ClientTeamMemberSelectWithDuplicateCheck from 'components/Inputs/FormInputs/ClientTeamMemberSelectWithDuplicateCheck';
import FormCheckbox from 'components/Inputs/FormInputs/FormCheckbox';
import TextField from 'components/Inputs/FormInputs/TextField';
import OrderRequiredFields from 'components/OrderRequiredFields';
import { SectionHeader } from 'components/SectionHeader';
import {
  ClientPod,
  CreateAmadeusBookingClientInput,
  HotelBookingPricing,
  HotelBookingRecoveryAction,
  RequiredFieldInput,
  useCreateAmadeusBookingMutation,
  useHotelOfferDetailsWithSessionQuery,
} from 'generated/graphql';
import { useClientRequiredFieldsAndPod } from 'hooks/useClientRequiredFieldsAndPod';
import { useUserRole } from 'hooks/useUserRole';
import { useAppSelector } from 'store/store';
import { formatErrorForDisplay } from 'utilities/errorMessages';
import {
  getContactEmailByPod,
  THS_PHONE_NUMBER,
} from 'utilities/getContactEmailByPod';
import {
  initializeRequiredFieldsArray,
  getOrderLevelFields,
} from 'utilities/guestFieldMapping';
import { calculateTotalGuests } from 'utilities/guestQuantityHelpers';
import { popToast } from 'utilities/popToast';
import { isExpiredPricingError } from 'utilities/pricingExpiration';
import { scrollToFirstError } from 'utilities/scrollToFirstError';

import { useHotelBookingTracking } from '../../hotel/bookingAttempts';
import {
  readBookingIntent,
  resolveBookingIntentSource,
  retainBookingIntent,
  retainBookingIntentSource,
} from '../../hotel/bookingIntentLineage';
import { useHotelSavedCardSelection } from '../../hotel/useHotelSavedCardSelection';
import { DuplicateBookingData } from '../../types/duplicateBooking';

import OfferDetailsCard from './OfferDetails';
import { SavedCardSelection } from './SavedCardSelection';
import { TripSummarySkeleton } from './TripSummarySkeleton';

// SelfBooking form values interface
interface SelfBookingFormValues {
  requiredFields: RequiredFieldInput[];
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  rewards?: string;
  paymentMethod?: string;
  skipGuestInfo?: boolean;
  // Guest assignment arrays
  [key: string]: unknown; // For dynamic guest fields like guests[0][0], guests[0][1], etc.
}

// Common styles
const INPUT_FIELD_CLASSES =
  'mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm';

// Simple wrapper for common TextField pattern
interface FormFieldProps {
  name: string;
  labelText: string;
  type?: string;
  [key: string]: unknown;
}

const FormField = ({
  name,
  labelText,
  type = 'text',
  ...props
}: FormFieldProps) => (
  <Field
    name={name}
    component={TextField}
    labelText={labelText}
    type={type}
    className={INPUT_FIELD_CLASSES}
    {...props}
  />
);

const SelfBookingForm = ({
  selectionIdentity,
}: {
  selectionIdentity: string;
}) => {
  const navigate = useNavigate();
  const { user: authUser } = useAuth0();
  const tracking = useHotelBookingTracking();
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  const [previewOverride, setPricingOverride] =
    useState<HotelBookingPricing | null>(null);
  const [previewChanged, setPricingChanged] = useState(false);
  const [preparingRetry, setPreparingRetry] = useState(false);
  const [resolutionRetry, setResolutionRetry] = useState<{
    requestKey: string;
    bookingId?: string;
  } | null>(null);
  const [retryingResolution, setRetryingResolution] = useState(false);
  const location = useLocation();
  const bookingDetails = location.state?.bookingDetails || {};
  const routeBookingIntentId =
    typeof location.state?.bookingIntentId === 'string'
      ? location.state.bookingIntentId
      : undefined;
  const entryKey = location.key;
  const inheritedSourceKey = location.state?.bookingSourceKey as
    | string
    | null
    | undefined;

  // State for confirmation modal
  const [showDuplicateConfirmModal, setShowDuplicateConfirmModal] =
    useState(false);

  // State to track duplicate bookings by field name
  const [duplicateBookings, setDuplicateBookings] = useState<
    Map<string, DuplicateBookingData>
  >(new Map());

  // State for persistent error messages
  const [persistentError, setPersistentError] = useState<string | null>(null);
  const [recoveryError, setRecoveryError] = useState<string | null>(null);

  // State for expired pricing
  const [isPricingExpired, setIsPricingExpired] = useState(false);
  const [refreshingPricing, setRefreshingPricing] = useState(false);

  // Store current form values in ref for modal access
  const currentFormValuesRef = useRef<SelfBookingFormValues | null>(null);

  // Get user data from Redux state
  const user = useAppSelector((state) => state.user);
  const userProfile = useAppSelector((state) => state.user.profile);
  const { isOnlyTraveler, canSelectGuests } = useUserRole();
  const lineageScope = useMemo(
    () =>
      authUser?.sub && userProfile?.clientId
        ? { actorId: authUser.sub, clientId: userProfile.clientId }
        : null,
    [authUser?.sub, userProfile?.clientId],
  );
  const [lineageRevision, setLineageRevision] = useState(0);
  const [lineageWriteFailed, setLineageWriteFailed] = useState(false);
  const [winningIntent, setWinningIntent] = useState<string | undefined>();
  const [retainedLineage, setRetainedLineage] = useState<{
    entryKey: string;
    sourceKey: string;
    intentId: string;
  } | null>(null);
  const normalizingEntry =
    entryKey === 'default' && inheritedSourceKey !== null;
  useEffect(() => {
    if (normalizingEntry)
      navigate(location.pathname, { replace: true, state: location.state });
  }, [normalizingEntry, navigate, location.pathname, location.state]);
  const lineage = useMemo(() => {
    // A user-requested check rereads this external storage snapshot.
    void lineageRevision;
    if (!lineageScope || normalizingEntry)
      return { sourceKey: null, intentId: null, failed: false };
    try {
      const sourceKey = resolveBookingIntentSource(
        lineageScope,
        entryKey,
        inheritedSourceKey,
      );
      return {
        sourceKey,
        intentId: readBookingIntent(lineageScope, sourceKey),
        failed: false,
      };
    } catch {
      return { sourceKey: null, intentId: null, failed: true };
    }
  }, [
    lineageScope,
    entryKey,
    inheritedSourceKey,
    normalizingEntry,
    lineageRevision,
  ]);
  // Persisting the returned intent on this same route must not fetch a second preview.
  const initialPricingIntent = useRef<{
    sourceKey: string;
    intentId?: string;
  } | null>(null);
  if (
    lineage.sourceKey &&
    initialPricingIntent.current?.sourceKey !== lineage.sourceKey
  )
    initialPricingIntent.current = {
      sourceKey: lineage.sourceKey,
      intentId: lineage.intentId ?? routeBookingIntentId,
    };
  const lineageFailed = lineage.failed || lineageWriteFailed;
  const canRetryLineage = lineageFailed && inheritedSourceKey !== null;

  // Get required fields for this client
  const { clientRequiredFields, clientPod } = useClientRequiredFieldsAndPod();

  // Check if there are order-level fields to display
  const hasOrderLevelFields = useMemo(() => {
    const orderFields = getOrderLevelFields(
      clientRequiredFields.map((field) => field.value),
    );
    return orderFields.length > 0;
  }, [clientRequiredFields]);

  // Determine default email based on pod
  const defaultEmail = useMemo(() => {
    return getContactEmailByPod(clientPod ?? ClientPod.Other);
  }, [clientPod]);

  // Initialize form values with required fields array - memoized to prevent form resets
  const initialValues = useMemo(() => {
    return {
      requiredFields: initializeRequiredFieldsArray(
        clientRequiredFields.map((field) => field.value),
      ),
      firstName: 'Team Housing',
      lastName: 'Solutions',
      email: defaultEmail,
      phone: THS_PHONE_NUMBER,
    };
  }, [clientRequiredFields, defaultEmail]);

  // Setup mutation
  const [createAmadeusBooking, { loading: bookingLoading }] =
    useCreateAmadeusBookingMutation();

  // Extracting booking details from location state
  const { hotel, offer } = bookingDetails;

  const {
    data,
    previousData,
    loading,
    refetch: refreshPricing,
  } = useHotelOfferDetailsWithSessionQuery({
    notifyOnNetworkStatusChange: true,
    variables: {
      amadeusHotelId: hotel?.amadeusHotelId || '',
      bookingCode: offer?.bookingCode || '',
      roomBedType: offer?.roomBedType || '',
      roomBeds: offer?.roomBeds || '',
      roomTypeCode: offer?.roomTypeCode || '',
      checkInDate: offer?.checkInDate || '',
      checkOutDate: offer?.checkOutDate || '',
      roomQuantity: offer?.rooms || 0,
      guestQuantity: offer?.guestsPerRoom || 0,
      bookingIntentId: winningIntent ?? initialPricingIntent.current?.intentId,
    },
    skip: !hotel || !offer || !lineage.sourceKey || !lineageScope,
    onError: (error) => {
      // Check if this is an expired pricing error (check both error code and message)
      if (isExpiredPricingError(error)) {
        setIsPricingExpired(true);
        setPersistentError(null); // Don't show generic error, show recovery UI instead
      } else {
        const message = `Failed to load offer details: ${formatErrorForDisplay(error)}`;
        setPersistentError(message);
      }
    },
  });

  const { offer: liveOffer, pricing } =
    data?.hotelOfferDetailsWithSession || {};
  // Cached details are display-only while a refresh is pending. Submission
  // still requires the current response and its retained pricing intent.
  const summary =
    data?.hotelOfferDetailsWithSession ??
    previousData?.hotelOfferDetailsWithSession;
  const preview = previewOverride ?? pricing?.bookingPricing;
  const bookingIntentId =
    winningIntent ??
    lineage.intentId ??
    routeBookingIntentId ??
    preview?.bookingIntentId;
  const lineageReady =
    !lineageFailed &&
    Boolean(
      preview &&
        retainedLineage?.entryKey === entryKey &&
        retainedLineage.sourceKey === lineage.sourceKey &&
        retainedLineage.intentId === preview.bookingIntentId,
    );
  useEffect(() => {
    const sourceKey = lineage.sourceKey;
    const intentId = preview?.bookingIntentId;
    if (!lineageScope || !sourceKey || !intentId) return;
    let abandoned = false;
    void (async () => {
      try {
        await retainBookingIntentSource(lineageScope, entryKey, sourceKey);
        const winner = await retainBookingIntent(
          lineageScope,
          sourceKey,
          intentId,
        );
        if (abandoned) return;
        setLineageWriteFailed(false);
        if (winner !== intentId) {
          setRetainedLineage(null);
          setPricingOverride(null);
          setWinningIntent(winner);
          return;
        }
        setRetainedLineage({ entryKey, sourceKey, intentId });
      } catch {
        if (!abandoned) setLineageWriteFailed(true);
      }
    })();
    return () => {
      abandoned = true;
    };
  }, [
    lineageScope,
    lineage.sourceKey,
    preview?.bookingIntentId,
    entryKey,
    lineageRevision,
  ]);
  const trackBookingIntent = tracking.trackIntent;
  useEffect(() => {
    if (!bookingIntentId) return;
    trackBookingIntent(bookingIntentId);
    return () => trackBookingIntent(null);
  }, [bookingIntentId, trackBookingIntent]);
  useEffect(() => {
    if (
      !lineageReady ||
      !retainedLineage ||
      (routeBookingIntentId === retainedLineage.intentId &&
        inheritedSourceKey === retainedLineage.sourceKey)
    )
      return;
    navigate(location.pathname, {
      replace: true,
      state: {
        ...location.state,
        bookingIntentId: retainedLineage.intentId,
        bookingSourceKey: retainedLineage.sourceKey,
      },
    });
  }, [
    location.pathname,
    location.state,
    navigate,
    lineageReady,
    retainedLineage,
    inheritedSourceKey,
    routeBookingIntentId,
  ]);
  const offerUnavailable =
    isPricingExpired ||
    (tracking.attempt?.status === 'FAILED' &&
      tracking.attempt.failureCode === 'OFFER_UNAVAILABLE' &&
      tracking.attempt.pricingId === preview?.id);
  const replacement =
    tracking.attempt?.status === 'RECONFIRMATION_REQUIRED' &&
    (tracking.attempt.pricingId === preview?.id ||
      (!previewOverride &&
        tracking.requestKey === tracking.attempt.requestKey &&
        tracking.attempt.bookingIntentId === preview?.bookingIntentId))
      ? tracking.attempt.replacementPricing
      : null;
  const handledReplacement = useRef<string | null>(null);
  const redirectedRequest = useRef<string | null>(null);
  const checkoutIdentity = `${user.id}:${userProfile?.clientId}:${routeBookingIntentId ?? bookingIntentId}:${hotel?.amadeusHotelId}:${offer?.bookingCode}`;
  // Hydrating the server intent must not reset the user's card choice or form.
  // Keep the intent-sensitive identity above for fencing booking callbacks.
  const ownCards = useHotelSavedCardSelection(selectionIdentity);
  const currentCheckout = useRef(checkoutIdentity);
  currentCheckout.current = checkoutIdentity;
  const { clearResolved, confirmed } = tracking;
  const handledFailure = useRef<string | null>(null);
  useEffect(() => {
    const attempt = tracking.attempt;
    // Search history keeps the intent across rate/hotel changes. A fresh
    // preview may retire a known failure, but never an unresolved request.
    if (
      attempt?.status !== 'FAILED' ||
      !preview ||
      loading ||
      !lineageReady ||
      !tracking.ready ||
      tracking.pending ||
      attempt.bookingIntentId !== preview.bookingIntentId ||
      attempt.pricingId === preview.id ||
      tracking.isPricingConsumed(preview.id) ||
      (tracking.requestKey && tracking.requestKey !== attempt.requestKey) ||
      handledFailure.current === attempt.requestKey
    )
      return;
    handledFailure.current = attempt.requestKey;
    const checkout = checkoutIdentity;
    const retainResolution = () => {
      if (active.current && currentCheckout.current === checkout)
        setResolutionRetry({ requestKey: attempt.requestKey });
    };
    void clearResolved(attempt.requestKey)
      .then((cleared) => {
        if (!cleared) retainResolution();
      })
      .catch(retainResolution);
  }, [
    tracking,
    preview,
    loading,
    lineageReady,
    clearResolved,
    checkoutIdentity,
  ]);
  useEffect(() => {
    const attempt = tracking.attempt;
    if (
      !replacement ||
      !attempt ||
      handledReplacement.current === replacement.id ||
      (tracking.requestKey && tracking.requestKey !== attempt.requestKey)
    )
      return;
    handledReplacement.current = replacement.id;
    setPricingOverride(replacement);
    setPricingChanged(true);
    setPersistentError(null);
    const checkout = checkoutIdentity;
    const retainResolution = () => {
      if (active.current && currentCheckout.current === checkout)
        setResolutionRetry({ requestKey: attempt.requestKey });
    };
    // Clear only this resolved submission. A newer cross-tab key must survive.
    void clearResolved(attempt.requestKey)
      .then((cleared) => {
        if (!cleared) retainResolution();
      })
      .catch(retainResolution);
  }, [
    replacement,
    tracking.attempt,
    tracking.requestKey,
    clearResolved,
    checkoutIdentity,
  ]);
  useEffect(() => {
    const attempt = tracking.attempt;
    if (
      attempt?.status !== 'CONFIRMED' ||
      !attempt.bookingId ||
      attempt.bookingIntentId !== bookingIntentId ||
      (tracking.requestKey && tracking.requestKey !== attempt.requestKey) ||
      redirectedRequest.current === attempt.requestKey
    )
      return;
    redirectedRequest.current = attempt.requestKey;
    const checkout = checkoutIdentity;
    const retainResolution = () => {
      if (active.current && currentCheckout.current === checkout)
        setResolutionRetry({
          requestKey: attempt.requestKey,
          bookingId: attempt.bookingId!,
        });
    };
    void confirmed(attempt.requestKey)
      .then((cleared) => {
        if (!cleared) retainResolution();
        else if (active.current && currentCheckout.current === checkout)
          navigate(`/booking-confirmation/${attempt.bookingId}`);
      })
      .catch(retainResolution);
  }, [
    bookingIntentId,
    checkoutIdentity,
    tracking.attempt,
    tracking.requestKey,
    confirmed,
    navigate,
  ]);

  const receiptUnknown = Boolean(tracking.requestKey && !tracking.attempt);
  const outcomeUnknown = tracking.attempt?.status === 'RECONCILIATION_REQUIRED';
  const safeFailure = tracking.attempt?.status === 'FAILED';
  const recoveryAction = safeFailure
    ? (tracking.attempt?.recoveryAction ??
      (offerUnavailable
        ? HotelBookingRecoveryAction.RefreshPricing
        : HotelBookingRecoveryAction.ContactSupport))
    : null;
  const canRefreshFailedBooking =
    recoveryAction === HotelBookingRecoveryAction.RefreshPricing;
  const showBookingSupport =
    outcomeUnknown ||
    (safeFailure && !tracking.pending && !canRefreshFailedBooking);
  const needsResolutionRetry = Boolean(
    resolutionRetry &&
      (!tracking.requestKey ||
        tracking.requestKey === resolutionRetry.requestKey),
  );
  const statusMessage = outcomeUnknown
    ? 'Please contact THS before trying again.'
    : bookingLoading || tracking.attempt?.status === 'PROCESSING'
      ? null
      : offerUnavailable
        ? 'This offer is no longer available. Refresh pricing or choose another rate.'
        : safeFailure
          ? canRefreshFailedBooking
            ? (tracking.attempt?.failureMessage ??
              'Refresh pricing before trying again.')
            : 'Contact THS for assistance.'
          : receiptUnknown
            ? 'The response was interrupted. We are checking whether this booking was received.'
            : previewChanged
              ? 'The price or terms changed. Review the updated summary, then select “Book this trip” to accept the changes and complete your booking.'
              : null;
  const hasBookingFeedback = Boolean(
    statusMessage ||
      persistentError ||
      recoveryError ||
      lineageFailed ||
      needsResolutionRetry ||
      tracking.notice ||
      tracking.statusUnavailable,
  );
  const feedbackStatus =
    persistentError || recoveryError || offerUnavailable || safeFailure
      ? 'error'
      : outcomeUnknown ||
          receiptUnknown ||
          previewChanged ||
          lineageFailed ||
          needsResolutionRetry ||
          tracking.notice ||
          tracking.statusUnavailable
        ? 'warning'
        : 'info';
  const feedbackTitle =
    outcomeUnknown || receiptUnknown
      ? 'Booking status unconfirmed'
      : offerUnavailable || safeFailure || previewChanged
        ? 'Booking not completed'
        : persistentError || recoveryError
          ? 'Unable to continue booking'
          : null;

  // Calculate total guests for display in page context
  const totalGuests = offer?.rooms
    ? calculateTotalGuests(offer.guestsPerRoom, offer.rooms)
    : null;

  const handleDuplicateStatusChange = useCallback(
    (
      fieldName: string,
      hasDuplicates: boolean,
      data?: DuplicateBookingData,
    ) => {
      setDuplicateBookings((prev) => {
        const newMap = new Map(prev);
        if (hasDuplicates && data) {
          newMap.set(fieldName, data);
        } else {
          newMap.delete(fieldName);
        }
        return newMap;
      });
    },
    [],
  );

  const hasDuplicateBookings = duplicateBookings.size > 0;

  const proceedWithBooking = async (values: SelfBookingFormValues) => {
    // Validate required user data
    if (!user.id || !user.profile?.clientId) {
      const message = 'User authentication required. Please log in again.';
      setPersistentError(message);
      return;
    }

    if (isOnlyTraveler && !userProfile?.clientTeamMemberId) {
      const message =
        'Your account must be linked to a team member profile to make bookings. Please contact your administrator.';
      setPersistentError(message);
      return;
    }

    if (
      !liveOffer ||
      !pricing ||
      !hotel ||
      !preview ||
      !lineageReady ||
      preview.bookingIntentId !== bookingIntentId ||
      loading ||
      refreshingPricing ||
      needsResolutionRetry ||
      offerUnavailable ||
      tracking.isPricingConsumed(preview.id) ||
      tracking.requestKey ||
      tracking.pending ||
      !tracking.ready ||
      !ownCards.ready
    ) {
      const message = 'Booking data is incomplete. Please try again.';
      setPersistentError(message);
      return;
    }

    let submittedKey: string | null = null;
    const submittedCheckout = checkoutIdentity;
    try {
      // Extract required fields - no transformation needed!
      const requiredFieldValues =
        values.requiredFields?.filter(
          (field) => field.value && field.value.trim() !== '',
        ) || [];

      // Transform form data to mutation input format
      const input: CreateAmadeusBookingClientInput = {
        // User context from Redux state
        clientId: user.profile.clientId,
        pricingId: preview.id,
        pricingFingerprint: preview.fingerprint,
        bookingIntentId: preview.bookingIntentId,
        requestKey: '',

        // Required field values
        requiredFields: requiredFieldValues,

        // Hotel and offer data
        amadeusHotelId: hotel.amadeusHotelId,
        bookingCode: liveOffer.bookingCode,
        rateCode: liveOffer.rateCode,
        roomTypeCode: liveOffer.roomTypeCode,
        roomBeds: liveOffer.roomBeds,

        // Dates and room details
        checkInDate: liveOffer.checkInDate,
        checkOutDate: liveOffer.checkOutDate,
        rooms: liveOffer.rooms,
        guestsPerRoom: liveOffer.guestsPerRoom,

        // Booking holder information
        bookingHolder: {
          firstName: values.firstName as string,
          lastName: values.lastName as string,
          email: values.email as string,
          phone: values.phone as string,
          hotelRewardsNumber: (values.rewards as string) || undefined,
        },

        // Transform guests data to bed-level occupancy format
        // Each room array has one entry per bed (guest ID or null)
        // For travelers: auto-assign themselves using clientTeamMemberId
        // Note: Traveler clientTeamMemberId is validated above in proceedWithBooking
        guestsByRoom:
          !canSelectGuests && userProfile?.clientTeamMemberId
            ? [[String(userProfile.clientTeamMemberId)]]
            : transformGuestsData(
                values.guests as ClientTeamMemberSelectOption[][],
                liveOffer.rooms,
                liveOffer.roomBeds, // Use roomBeds for bed-level occupancy
                values.skipGuestInfo as boolean,
              ),

        // Location data from hotel
        location: {
          city: hotel.address.city || '',
          latitude: hotel.latitude,
          longitude: hotel.longitude,
        },

        // Payment and other details
        paymentMethod: preview.paymentMethod,
        travelerPaymentMethodId: ownCards.required
          ? ownCards.selectedId
          : undefined,

        // Skip guest info flag - backend will use unknown guest if true
        skipGuestInfo: values.skipGuestInfo || undefined,

        notes: undefined, // Could add notes field to form if needed
      };

      submittedKey = await tracking.begin();
      if (!active.current || currentCheckout.current !== submittedCheckout)
        return;
      input.requestKey = submittedKey;
      setPricingChanged(false);
      const result = await createAmadeusBooking({ variables: { input } });
      if (result.data?.createAmadeusBooking) {
        const bookingId = result.data.createAmadeusBooking.id;
        let cleared = false;
        try {
          cleared = await tracking.confirmed(submittedKey);
        } catch {
          /* Keep the known result available below. */
        }
        if (!active.current || currentCheckout.current !== submittedCheckout)
          return;
        redirectedRequest.current = submittedKey;
        if (!cleared) {
          setResolutionRetry({ requestKey: submittedKey, bookingId });
          return;
        }
        popToast(
          `Booking confirmed! Confirmation #: ${result.data.createAmadeusBooking.confirmationNumber}`,
          { type: 'success' },
        );
        navigate(
          `/booking-confirmation/${result.data.createAmadeusBooking.id}`,
        );
      }
    } catch (error) {
      if (!active.current || currentCheckout.current !== submittedCheckout)
        return;
      if (submittedKey) await tracking.observeError(error, submittedKey);
      if (!active.current || currentCheckout.current !== submittedCheckout)
        return;
      const failed = (
        error as { graphQLErrors?: { extensions?: { code?: string } }[] }
      )?.graphQLErrors?.some(
        (item) => item.extensions?.code === 'HOTEL_BOOKING_FAILED',
      );
      setPersistentError(
        submittedKey && !failed ? null : formatErrorForDisplay(error),
      );
    }
  };

  const handleBooking = async (values: SelfBookingFormValues) => {
    // Clear persistent error when form is submitted
    setPersistentError(null);
    setRecoveryError(null);

    // Check if there are any duplicate bookings
    if (hasDuplicateBookings) {
      // Show confirmation modal (values will be accessed directly from form)
      setShowDuplicateConfirmModal(true);
    } else {
      // No duplicates, proceed with booking
      await proceedWithBooking(values);
    }
  };

  const handleDuplicateConfirmation = async () => {
    setShowDuplicateConfirmModal(false);
    if (currentFormValuesRef.current) {
      await proceedWithBooking(currentFormValuesRef.current);
    }
  };

  const handleDuplicateCancel = () => {
    setShowDuplicateConfirmModal(false);
  };

  const handleRefreshPricing = async () => {
    if (tracking.pending || refreshingPricing) return;
    const checkout = checkoutIdentity;
    setRecoveryError(null);
    setRefreshingPricing(true);
    try {
      const cleared = await tracking.clearResolved();
      if (!active.current || currentCheckout.current !== checkout) return;
      if (!cleared) {
        setRecoveryError(
          'We could not prepare this booking. Please try again or contact THS for help.',
        );
        return;
      }
      setPricingOverride(null);
      setPricingChanged(false);
      setPersistentError(null);
      await refreshPricing({
        bookingIntentId,
      });
      if (!active.current || currentCheckout.current !== checkout) return;
      setIsPricingExpired(false);
    } catch (error) {
      if (!active.current || currentCheckout.current !== checkout) return;
      if (isExpiredPricingError(error as Error)) setIsPricingExpired(true);
      else setRecoveryError(formatErrorForDisplay(error as Error));
    } finally {
      if (active.current) setRefreshingPricing(false);
    }
  };

  const handleTryAgain = async () => {
    if (preparingRetry) return;
    setPreparingRetry(true);
    const intent = bookingIntentId;
    const checkout = checkoutIdentity;
    try {
      const closed = await tracking.closeUnreceived();
      if (!closed || !active.current || currentCheckout.current !== checkout)
        return;
      // Preparing a retry never submits. Only an acknowledged fence permits fresh pricing.
      setRefreshingPricing(true);
      setPricingOverride(null);
      setPricingChanged(false);
      setPersistentError(null);
      await refreshPricing({ bookingIntentId: intent });
      if (active.current && currentCheckout.current === checkout)
        setIsPricingExpired(false);
    } catch (error) {
      if (active.current && currentCheckout.current === checkout)
        setPersistentError(formatErrorForDisplay(error));
    } finally {
      if (active.current) {
        setPreparingRetry(false);
        setRefreshingPricing(false);
      }
    }
  };

  const handleCheckAgain = async () => {
    if (canRetryLineage) {
      setLineageRevision((revision) => revision + 1);
      return;
    }
    if (!needsResolutionRetry || !resolutionRetry) {
      await tracking.refresh();
      return;
    }
    if (retryingResolution) return;
    const checkout = checkoutIdentity;
    setRetryingResolution(true);
    try {
      await tracking.refresh();
      const cleared = resolutionRetry.bookingId
        ? await tracking.confirmed(resolutionRetry.requestKey)
        : await tracking.clearResolved(resolutionRetry.requestKey);
      if (!active.current || currentCheckout.current !== checkout || !cleared)
        return;
      setResolutionRetry(null);
      if (resolutionRetry.bookingId)
        navigate(`/booking-confirmation/${resolutionRetry.bookingId}`);
    } catch {
      // Leave the matching result and key in place so this check remains repeatable.
    } finally {
      if (active.current) setRetryingResolution(false);
    }
  };

  // Validation function to ensure either guests are added or skipGuestInfo is selected
  const validateGuestSelection = (
    values: SelfBookingFormValues,
  ): Record<string, string> => {
    const errors: Record<string, string> = {};

    // Travelers are auto-assigned, no validation needed
    if (!canSelectGuests) {
      return errors;
    }

    // If skipGuestInfo is checked, validation passes
    if (values.skipGuestInfo) {
      return errors;
    }

    // Check if at least one guest has been added
    const guests = values.guests as
      | ClientTeamMemberSelectOption[][]
      | undefined;
    if (!guests || guests.length === 0) {
      errors._guestSelection =
        'Please either add guests to rooms or select "Skip for now"';
      return errors;
    }

    // Check if at least one guest has an ID (is actually selected)
    const hasAtLeastOneGuest = guests.some((roomGuests) =>
      roomGuests?.some(
        (guest) =>
          guest && typeof guest === 'object' && 'id' in guest && guest.id,
      ),
    );

    if (!hasAtLeastOneGuest) {
      errors._guestSelection =
        'Please either add guests to rooms or select "Skip for now"';
    }

    return errors;
  };

  // Helper function to transform guests data to bed-level occupancy format
  // Returns (string | null)[][] where string = guest ID, null = empty bed
  // Each room array has one entry per bed (from roomBeds)
  // When skipGuestInfo is true, returns all nulls (backend will fill with unknown guest)
  const transformGuestsData = (
    guestsForm: ClientTeamMemberSelectOption[][] | undefined,
    rooms: number,
    bedsPerRoom: number,
    skipGuestInfo: boolean,
  ): (string | null)[][] => {
    if (skipGuestInfo || !guestsForm) {
      // Return arrays with nulls for each bed when skipping guest info
      // Backend will fill with unknown guest ID
      return Array(rooms)
        .fill(null)
        .map(() => Array(bedsPerRoom).fill(null));
    }

    return Array(rooms)
      .fill(null)
      .map((_, roomIndex) => {
        const roomGuests = guestsForm[roomIndex] || [];
        const bedOccupancy: (string | null)[] = [];

        // Map guests to beds (one guest per bed)
        for (let bedIndex = 0; bedIndex < bedsPerRoom; bedIndex++) {
          const guest = roomGuests[bedIndex];
          // Extract ID string if guest exists and has an ID
          // GuestInput can be { id: string } or { firstName: string; lastName: string }
          if (
            guest &&
            'id' in guest &&
            guest.id &&
            typeof guest.id === 'string'
          ) {
            bedOccupancy.push(guest.id);
          } else {
            bedOccupancy.push(null);
          }
        }

        return bedOccupancy;
      });
  };

  return (
    <div className="flex flex-col items-center p-5 sm:px-28">
      <div className="w-full max-w-7xl">
        <SectionHeader headline="Finish Your Booking">
          {totalGuests && (
            <span className="text-sm text-gray-600">
              {totalGuests} guests • {offer.rooms} room
              {offer.rooms === 1 ? '' : 's'}
            </span>
          )}
        </SectionHeader>

        <Form
          onSubmit={handleBooking}
          initialValues={initialValues}
          validate={validateGuestSelection}
          mutators={
            // NOTE: Type assertion required due to complex final-form-arrays generic constraints.
            // arrayMutators provides array field manipulation methods (push, pop, etc.)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- final-form-arrays types are complex
            arrayMutators as Record<string, any>
          }
          render={({
            handleSubmit,
            values,
            errors,
            submitFailed,
            submitting,
            hasValidationErrors,
          }) => {
            // Store current form values in ref for modal access
            currentFormValuesRef.current = values;

            // Enhanced submit handler that scrolls to first error on validation failure
            const enhancedHandleSubmit = (
              event: React.FormEvent<HTMLFormElement>,
            ) => {
              if (hasValidationErrors && errors) {
                scrollToFirstError(errors as Record<string, unknown>);
              }
              handleSubmit(event);
            };

            return (
              <form
                onSubmit={enhancedHandleSubmit}
                className="grid grid-cols-1 gap-6 md:grid-cols-3"
              >
                {/* Booking Details - Only show if there are required fields */}
                {hasOrderLevelFields && <OrderRequiredFields />}

                {/* Booking Holder */}
                <Card className="col-span-2">
                  <h2 className="mb-4 text-base font-bold">Booking Holder</h2>
                  <div>
                    <FormField name="rewards" labelText="Hotel rewards #" />
                  </div>
                </Card>

                {/* Booking Holder - Hidden fields with THS defaults */}
                <div className="hidden">
                  <FormField name="firstName" labelText="First Name" />
                  <FormField name="lastName" labelText="Last Name" />
                  <FormField name="email" labelText="Email" type="email" />
                  <FormField name="phone" labelText="Phone #" type="tel" />
                </div>

                {/* Guest Information - Only shown for users who can select guests */}
                {canSelectGuests && (
                  <Card className="col-span-2  overflow-visible">
                    <h2 className="mb-4 text-base font-bold">
                      Add Guests to Room
                    </h2>
                    <Field
                      name="skipGuestInfo"
                      labelClassName="text-sm"
                      labelText="Skip for now (You will be required to add the guest later from the trips page after you complete the booking)"
                      component={FormCheckbox}
                      type="checkbox"
                      className="mb-4"
                    />
                    {errors._guestSelection && submitFailed && (
                      <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3">
                        <p className="text-sm text-red-600">
                          {errors._guestSelection}
                        </p>
                      </div>
                    )}
                    {offer?.rooms &&
                      times(offer.rooms, (index) => (
                        <div
                          key={index}
                          className="mb-4 rounded-md border border-gray-200 bg-gray-50 p-4 hover:bg-primaryGray/10"
                        >
                          <div className="mb-2 text-sm text-gray-700">
                            <span className="font-semibold">
                              Room {index + 1} ({offer.roomBeds}{' '}
                              {offer.roomBedType || 'N/A'} bed •{' '}
                              {offer.guestsPerRoom || 'N/A'} guests)
                            </span>
                          </div>
                          {times(offer.guestsPerRoom, (roomIndex) => (
                            <ClientTeamMemberSelectWithDuplicateCheck
                              key={roomIndex}
                              name={`guests[${index}][${roomIndex}]`}
                              disabled={!!values.skipGuestInfo}
                              allowCreateGuest={true}
                              checkInDate={offer.checkInDate}
                              checkOutDate={offer.checkOutDate}
                              onDuplicateStatusChange={
                                handleDuplicateStatusChange
                              }
                            />
                          ))}
                        </div>
                      ))}
                  </Card>
                )}

                {/* The supplier method still goes to the API; only its confusing display is hidden for own-card clients. */}
                {ownCards.required === false && (
                  <Card className="col-span-2">
                    <h2 className="mb-4 text-base font-bold">
                      Payment Information
                    </h2>
                    <div className="mb-4">
                      <FormField
                        name="paymentMethod"
                        labelText="Payment Method"
                        disabled
                        placeholder="Auto generated card"
                      />
                      <p className="mt-2 block text-sm font-normal text-primaryGray">
                        New card will be automatically created after submitting.
                        Details will be included in the confirmation.
                      </p>
                    </div>
                  </Card>
                )}
                <div className="col-span-2">
                  <SavedCardSelection
                    selection={ownCards}
                    identity={selectionIdentity}
                    locked={
                      bookingLoading ||
                      tracking.pending ||
                      Boolean(tracking.requestKey)
                    }
                  />
                </div>

                {/* Trip Summary */}
                <Card className="md:col-start-3 md:row-span-4 md:row-start-1">
                  <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-base font-bold">Your Trip</h2>
                    {!tracking.pending && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(-2)}
                      >
                        Modify trip
                      </Button>
                    )}
                  </div>
                  <div aria-busy={loading || refreshingPricing}>
                    {summary ? (
                      <OfferDetailsCard
                        preview={
                          previewOverride ?? summary.pricing.bookingPricing
                        }
                        booking={{
                          // Core offer fields
                          avgPrice: summary.offer.avgPrice,
                          basePrice: summary.offer.basePrice,
                          guestsPerRoom: summary.offer.guestsPerRoom,
                          rooms: summary.offer.rooms,
                          checkInDate: summary.offer.checkInDate,
                          checkOutDate: summary.offer.checkOutDate,
                          nights: summary.offer.nights,
                          roomBedType: summary.offer.roomBedType,
                          cancellationDetails:
                            summary.offer.cancellationDetails,
                          // Pricing fields from pricing object
                          estimatedTax: summary.pricing.grandTotalTax,
                          grandTotalAfterTax:
                            summary.pricing.grandTotalAfterTax,
                          // THS price with tax inclusion information
                          thsPrice: summary.offer.thsPrice,
                          // Hotel data
                          hotel: {
                            __typename: 'AmadeusHotel' as const,
                            name: hotel.name,
                            formattedAddress: hotel.formattedAddress,
                            imageUrl: hotel.imageUrl,
                          },
                        }}
                      />
                    ) : loading ? (
                      <TripSummarySkeleton />
                    ) : null}
                  </div>
                  {hasBookingFeedback && (
                    <Banner status={feedbackStatus} className="mt-4">
                      <div className="space-y-2">
                        {feedbackTitle && (
                          <p className="font-semibold">{feedbackTitle}</p>
                        )}
                        {persistentError && !safeFailure && (
                          <p>{persistentError}</p>
                        )}
                        {recoveryError && <p>{recoveryError}</p>}
                        {statusMessage && <p>{statusMessage}</p>}
                        {showBookingSupport && (
                          <div className="space-y-1 pt-1">
                            <a
                              href={`tel:${THS_PHONE_NUMBER}`}
                              className={`block w-fit whitespace-nowrap rounded-sm text-accent underline underline-offset-2 ${focusRing}`}
                            >
                              {THS_PHONE_NUMBER}
                            </a>
                            <a
                              href={`mailto:${defaultEmail}`}
                              aria-label={defaultEmail}
                              className={`block w-fit rounded-sm text-accent underline underline-offset-2 ${focusRing}`}
                            >
                              {defaultEmail.split('@')[0]}@
                              <wbr />
                              {defaultEmail.split('@')[1]}
                            </a>
                          </div>
                        )}
                        {lineageFailed && (
                          <p>
                            {inheritedSourceKey === null
                              ? "We couldn't retain this booking safely. Return to your previous search and choose your rate again."
                              : "We couldn't retain this booking safely in your browser. Check again before booking."}
                          </p>
                        )}
                        {needsResolutionRetry && (
                          <p>
                            {resolutionRetry?.bookingId
                              ? 'Your booking is confirmed, but it could not be opened. Please check again.'
                              : 'The current selection is ready, but we could not finish checking the previous request. Please check again before booking.'}
                          </p>
                        )}
                        {(tracking.notice || tracking.statusUnavailable) && (
                          <p>
                            {tracking.notice ??
                              'Booking status could not be checked. Please check again.'}
                          </p>
                        )}
                      </div>
                    </Banner>
                  )}
                  {!tracking.pending &&
                    (!safeFailure || canRefreshFailedBooking) &&
                    (offerUnavailable ||
                      canRefreshFailedBooking ||
                      (persistentError && !preview) ||
                      (preview && tracking.isPricingConsumed(preview.id))) && (
                      <Button
                        type="button"
                        variant="secondary"
                        className="mt-3"
                        disabled={loading || refreshingPricing}
                        onClick={handleRefreshPricing}
                      >
                        Refresh pricing
                      </Button>
                    )}
                  {!bookingLoading &&
                    !submitting &&
                    (tracking.statusUnavailable ||
                      canRetryLineage ||
                      needsResolutionRetry ||
                      outcomeUnknown ||
                      receiptUnknown) && (
                      <Button
                        type="button"
                        variant="ghost"
                        className="mt-3"
                        disabled={
                          tracking.checking ||
                          preparingRetry ||
                          retryingResolution
                        }
                        onClick={() => void handleCheckAgain()}
                      >
                        Check again
                      </Button>
                    )}
                  {!bookingLoading &&
                    !submitting &&
                    tracking.canCloseUnreceived && (
                      <Button
                        type="button"
                        variant="secondary"
                        className="mt-3"
                        disabled={
                          tracking.closing ||
                          preparingRetry ||
                          refreshingPricing
                        }
                        onClick={handleTryAgain}
                      >
                        Try again
                      </Button>
                    )}
                  <div className="mt-6 flex flex-col gap-2">
                    <Button
                      type="submit"
                      loading={
                        bookingLoading ||
                        tracking.attempt?.status === 'PROCESSING'
                      }
                      disabled={
                        !ownCards.ready ||
                        submitting ||
                        tracking.pending ||
                        loading ||
                        refreshingPricing ||
                        preparingRetry ||
                        needsResolutionRetry ||
                        !liveOffer ||
                        !preview ||
                        !lineageReady ||
                        preview.bookingIntentId !== bookingIntentId ||
                        tracking.isPricingConsumed(preview.id) ||
                        Boolean(tracking.requestKey) ||
                        !tracking.ready ||
                        offerUnavailable
                      }
                    >
                      {bookingLoading ||
                      tracking.attempt?.status === 'PROCESSING'
                        ? 'Booking…'
                        : 'Book this trip'}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => navigate(-1)}
                    >
                      Back
                    </Button>
                  </div>
                </Card>
              </form>
            );
          }}
        />

        {/* Duplicate Booking Confirmation Modal */}
        <DuplicateBookingConfirmationModal
          isOpen={showDuplicateConfirmModal}
          onClose={handleDuplicateCancel}
          onConfirm={handleDuplicateConfirmation}
          duplicateBookings={Array.from(duplicateBookings.values())}
        />
      </div>
    </div>
  );
};

const SelfBooking = () => {
  const { state, key } = useLocation();
  const { user: authUser } = useAuth0();
  const clientId = useAppSelector((store) => store.user.profile?.clientId);
  const { hotel, offer } = state?.bookingDetails ?? {};
  let sourceKey =
    state?.bookingSourceKey === undefined ? key : state.bookingSourceKey;
  if (authUser?.sub && clientId) {
    try {
      sourceKey = resolveBookingIntentSource(
        { actorId: authUser.sub, clientId },
        key,
        state?.bookingSourceKey,
      );
    } catch {
      // The form handles unavailable lineage inline. Preserve its fallback
      // identity while storage is unavailable or the entry is normalized.
    }
  }
  // In-place rate selection is a new checkout view. Do not carry replacement
  // terms, form fields or in-flight callbacks over from the previous offer.
  const selection = JSON.stringify([
    authUser?.sub,
    clientId,
    sourceKey,
    hotel?.amadeusHotelId,
    offer?.bookingCode,
    offer?.roomBedType,
    offer?.roomBeds,
    offer?.roomTypeCode,
    offer?.checkInDate,
    offer?.checkOutDate,
    offer?.rooms,
    offer?.guestsPerRoom,
  ]);
  return <SelfBookingForm key={selection} selectionIdentity={selection} />;
};

export default SelfBooking;
